// Things that need a look (slice 51a), and what can be done about them (51b,
// ADR 0047): acknowledge ("I have seen it"), resolve ("it is dealt with"), and,
// for the owner, replay the till record that was set aside. The buttons are
// drawn only for someone holding the permission; the server enforces every one.

import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type { PosException } from '../../api/pos-types.js';
import { Chip, QueryState, actionErrorText, type TabProps } from './common.js';
import { formatDateTime } from './format.js';

export interface ExceptionsTabProps extends TabProps {
  /** pos:exception_manage */
  readonly canWork: boolean;
  /** pos:event_replay */
  readonly canReplay: boolean;
}

type Action = 'acknowledge' | 'resolve' | 'replay';

export function ExceptionsTab({
  orgId,
  outletId,
  canWork,
  canReplay,
}: ExceptionsTabProps): JSX.Element {
  const qc = useQueryClient();
  const [state, setState] = useState('unresolved');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const listKey = ['pos-exceptions', orgId, outletId] as const;
  const errors = useQuery({
    queryKey: [...listKey, state],
    queryFn: () =>
      apiFetch<{ exceptions: PosException[] }>(
        `/organizations/${orgId}/outlets/${outletId}/pos/exceptions${state ? `?state=${state}` : ''}`,
      ),
  });

  const act = useMutation({
    mutationFn: (args: { id: string; action: Action }) => {
      const note = notes[args.id]?.trim();
      return apiFetch<unknown>(
        `/organizations/${orgId}/outlets/${outletId}/pos/exceptions/${args.id}/${args.action}`,
        { method: 'POST', body: note ? { note } : {} },
      );
    },
    onSettled: (_data, _err, args) => {
      setNotes((n) => ({ ...n, [args.id]: '' }));
      void qc.invalidateQueries({ queryKey: listKey });
      // A replay or a resolution can change what the day's report is waiting for.
      void qc.invalidateQueries({ queryKey: ['pos-day', orgId, outletId] });
    },
  });
  const error = actionErrorText(act.error);

  return (
    <section aria-labelledby="exceptions-heading">
      <h2 id="exceptions-heading">Things that need a look</h2>
      <label className="inline-field">
        Show{' '}
        <select value={state} onChange={(e) => setState(e.target.value)}>
          <option value="unresolved">Not resolved</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
          <option value="">Everything</option>
        </select>
      </label>
      <QueryState loading={errors.isLoading} error={errors.error} />
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      {errors.data && errors.data.exceptions.length === 0 ? (
        <p className="muted">Nothing needs your attention.</p>
      ) : null}
      <ul className="exception-list">
        {(errors.data?.exceptions ?? []).map((e) => (
          <li key={e.id} className="card">
            <div className="refund-head">
              <strong>{e.title}</strong>
              <Chip tone={e.state === 'resolved' ? 'ok' : 'warn'}>{e.state}</Chip>
            </div>
            <p className="muted">
              {formatDateTime(e.occurredAt)}
              {e.acknowledgedAt ? ` · acknowledged ${formatDateTime(e.acknowledgedAt)}` : ''}
              {e.resolvedAt ? ` · resolved ${formatDateTime(e.resolvedAt)}` : ''}
            </p>
            <p>{e.message}</p>
            {e.resolutionNote ? <p className="muted">Note: {e.resolutionNote}</p> : null}
            {e.state !== 'resolved' && (canWork || (canReplay && e.canReplay)) ? (
              <div className="exception-actions">
                <label className="inline-field">
                  Note (optional){' '}
                  <input
                    type="text"
                    maxLength={500}
                    value={notes[e.id] ?? ''}
                    onChange={(ev) => setNotes((n) => ({ ...n, [e.id]: ev.target.value }))}
                  />
                </label>
                <div className="row-actions">
                  {canWork && e.state === 'open' ? (
                    <button
                      type="button"
                      className="secondary"
                      disabled={act.isPending}
                      onClick={() => act.mutate({ id: e.id, action: 'acknowledge' })}
                    >
                      Acknowledge
                    </button>
                  ) : null}
                  {canWork ? (
                    <button
                      type="button"
                      disabled={act.isPending}
                      onClick={() => act.mutate({ id: e.id, action: 'resolve' })}
                    >
                      Mark resolved
                    </button>
                  ) : null}
                  {canReplay && e.canReplay ? (
                    <button
                      type="button"
                      className="secondary"
                      disabled={act.isPending}
                      onClick={() => act.mutate({ id: e.id, action: 'replay' })}
                    >
                      Replay the till record
                    </button>
                  ) : null}
                </div>
                {e.canReplay ? (
                  <p className="muted">
                    Replaying puts the record back in the queue and applies it again. Nothing that
                    already went out (a message, points, a refund) is sent twice.
                  </p>
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
